/**
 * Upload a file as a blob, using tus direct upload when available.
 *
 * Uploads must go through tus direct upload. The old blobSync fallback hashes
 * files with Blob.arrayBuffer(), which breaks the streamable blob contract.
 */
import type { BlockStdScope } from '@blocksuite/std';
import type { Store } from '@blocksuite/store';

import {
  TusDirectUploadProvider,
  type UploadProgressCallback,
} from '../services/tus-upload-service';

/**
 * Upload a file and return its blob key (SHA-256 hash).
 *
 * Usage (with BlockStdScope):
 *   const sourceId = await uploadBlob(std, file);
 *
 * Usage (with Store):
 *   const sourceId = await uploadBlobFromStore(store, file);
 */
export async function uploadBlob(
  std: BlockStdScope,
  file: File,
  onProgress?: UploadProgressCallback
): Promise<string> {
  const tusUpload = std.getOptional(TusDirectUploadProvider);
  // sl-rm09.4: Pass docId for immediate BlobDocMapping (doc-level RBAC)
  const docId = std.store.id;
  const key = tusUpload
    ? await tusUpload.upload(file, onProgress, docId)
    : null;
  if (key) return key;
  throw new Error('Streamable blob upload requires tus direct upload');
}

/**
 * Persist a file locally first and enqueue its tus upload.
 * Resolves after durable local staging rather than remote transfer.
 */
export async function stageBlob(
  std: BlockStdScope,
  file: File,
  onProgress?: UploadProgressCallback
): Promise<string> {
  const upload = std.getOptional(TusDirectUploadProvider);
  const key = upload
    ? await upload.stage(file, onProgress, std.store.id)
    : null;
  if (key) return key;
  throw new Error('Local-first blob staging requires the upload provider');
}

/**
 * Same as uploadBlob but accepts a Store directly (for call sites without BlockStdScope).
 */
export async function uploadBlobFromStore(
  store: Store,
  _file: File
): Promise<string> {
  throw new Error(
    `Streamable blob upload requires BlockStdScope with tus direct upload for store ${store.id}`
  );
}
